/*
 * seatingEngine — the client-side availability logic that powers the LIVE board.
 * Given the day's reservations, the real tables, a viewing time and the default seating
 * duration, it decides which tables are free / occupied / upcoming and which free table
 * best fits the next party. Pure functions — no side effects, easy to unit-test and to
 * swap the data source (mock now, reservationsList act later).
 *
 * NOTE on semantics: this engine uses a FORWARD interval [start, start+durationMin). The
 * current PHP engine instead applies maxTimeTableIsInUseInMinutes as a SYMMETRIC window
 * (start±minutes) — so client and server availability can differ until the PHP engine is
 * switched to forward-interval semantics (see docs/BACKEND_ACTS_SPEC.md).
 *
 * Conventions:
 *  - times are "HH:MM" strings; we work in minutes-since-midnight internally.
 *  - a reservation occupies its table for [start, start + durationMin) (forward interval).
 *  - "upcoming" = a reservation starts within UPCOMING_LEAD minutes of the viewing time.
 */

export const UPCOMING_LEAD = 30;

export function toMinutes(hhmm) {
    if (typeof hhmm !== 'string') return NaN;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return NaN;
    return h * 60 + m;
}

export function toHHMM(mins) {
    const m = ((Math.round(mins) % 1440) + 1440) % 1440;
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

// Seats a table offers (client table shape uses `diners`; tolerate `seats`).
export function tableSeats(table) {
    return Number(table?.diners ?? table?.seats ?? 0) || 0;
}

// The [start, end) window (in minutes) a reservation holds its table.
export function reservationWindow(res, durationMin) {
    const start = toMinutes(res.time);
    const dur = Number(res.durationMin || durationMin) || durationMin;
    return [start, start + dur];
}

/*
 * Effective seating duration for a SPECIFIC table. If the table carries a per-table hold
 * time (`holdTime`, זמן תפוסה) that is a positive finite number, use it; otherwise fall back
 * to the business default `durationMin`. When `table` is missing or has no override
 * (null/undefined/0), this returns `durationMin` unchanged — so behavior is IDENTICAL to
 * before this per-table feature existed.
 */
export function effectiveDuration(table, durationMin) {
    const h = Number(table?.holdTime);
    return (Number.isFinite(h) && h > 0) ? h : durationMin;
}

const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

/*
 * floorStateAt — status of every table at `atMinutes`.
 * Returns a Map: tableId -> { status: 'free'|'occupied'|'upcoming', res|null }.
 * A table is 'occupied' if a reservation assigned to it covers the viewing time;
 * 'upcoming' if one is assigned to it starting within UPCOMING_LEAD; else 'free'.
 */
export function floorStateAt(reservations, tables, atMinutes, durationMin, leadMin = UPCOMING_LEAD) {
    // Keys are stringified so a table id of 12 matches a reservation tableId of "12".
    // Keep the table object on each cell so we can honor its per-table hold time (holdTime).
    const byTable = new Map();
    (tables || []).forEach((t) => byTable.set(String(t.id), { status: 'free', res: null, table: t }));

    (reservations || []).forEach((r) => {
        const key = r.tableId == null ? null : String(r.tableId);
        if (key == null || !byTable.has(key)) return;
        if (r.status === 'cancelled' || r.status === 'no-show') return;
        const cell = byTable.get(key);
        // Occupancy window uses THIS table's effective duration (its holdTime override if any,
        // else the business default durationMin — identical to before when there's no override).
        const [s, e] = reservationWindow(r, effectiveDuration(cell.table, durationMin));
        if (atMinutes >= s && atMinutes < e) {
            cell.status = 'occupied';
            cell.res = r;
        } else if (cell.status === 'free' && s > atMinutes && s - atMinutes <= leadMin) {
            cell.status = 'upcoming';
            cell.res = r;
        }
    });
    return byTable;
}

// Is a specific table free for [at, at+dur) given the reservations?
// `table` is OPTIONAL and backward-compatible: when omitted (or it has no holdTime override),
// the effective duration is exactly `durationMin` — identical to the original behavior. When a
// table object with a positive `holdTime` is passed, that per-table hold time is used for BOTH
// the requested window and each reservation's occupancy window on this table.
export function isTableFree(reservations, tableId, atMinutes, durationMin, table = null) {
    const dur = effectiveDuration(table, durationMin);
    const [ws, we] = [atMinutes, atMinutes + dur];
    return !(reservations || []).some((r) => {
        if (r.tableId == null || String(r.tableId) !== String(tableId)) return false;
        if (r.status === 'cancelled' || r.status === 'no-show') return false;
        const [s, e] = reservationWindow(r, dur);
        return overlaps(ws, we, s, e);
    });
}

/*
 * suggestNextTable — the best free table for a party at a time (mirrors saveSlotTemporary):
 * filter by area (if given) + enough seats + free for the whole window, then prefer the
 * SMALLEST fitting table (least wasted seats), tie-broken by lowest id. Returns the table
 * or null if none.
 */
export function suggestNextTable(reservations, tables, { atMinutes, party, areaId = null, durationMin }) {
    const candidates = (tables || [])
        .filter((t) => !t.is_locked)
        .filter((t) => (areaId == null || Number(t.area_id) === Number(areaId)))
        .filter((t) => tableSeats(t) >= party)
        // Pass the table so its per-table holdTime (if any) is honored; else falls back to durationMin.
        .filter((t) => isTableFree(reservations, t.id, atMinutes, durationMin, t));
    if (!candidates.length) return null;
    candidates.sort((a, b) => (tableSeats(a) - tableSeats(b)) || (Number(a.id) - Number(b.id)));
    return candidates[0];
}

/*
 * recommendTableExplained — the product's core differentiator: PROPOSE the best table for a
 * party AND EXPLAIN WHY, in ASSISTIVE mode (system recommends, hostess confirms/overrides).
 * Deterministic rules only — never a black box. Reuses the SAME candidate set as suggestNextTable
 * (unlocked, in-area if areaId, seats>=party, free for the whole window), sorted smallest-fitting
 * first (least waste, tie-break lowest id). The CHOSEN table is candidates[0]; up to two next-best
 * FITTING free tables are offered as alternatives. Returns null (safely) if nothing fits.
 *
 * Shape: { table, score(0..100), reasons:[Hebrew string], alternatives:[{id,name,seats}] }.
 * PURE — does not mutate inputs and does not alter suggestNextTable/freeTablesFor behavior.
 */
export function recommendTableExplained(reservations, tables, { atMinutes, party, areaId = null, durationMin } = {}) {
    const p = Number(party) || 0;
    // Guard: without a valid party, table list, AND finite time/duration, availability can't be
    // computed correctly (isTableFree would treat every reservation as non-overlapping, and a
    // reason could read "undefined דק׳"). Bail to null so the card simply doesn't show.
    if (!Array.isArray(tables) || p <= 0 || !Number.isFinite(Number(atMinutes)) || !Number.isFinite(Number(durationMin))) return null;

    const candidates = tables
        .filter((t) => t && !t.is_locked)
        .filter((t) => (areaId == null || Number(t.area_id) === Number(areaId)))
        .filter((t) => tableSeats(t) >= p)
        // Pass the table so its per-table holdTime (if any) is honored; else falls back to durationMin.
        .filter((t) => isTableFree(reservations, t.id, atMinutes, durationMin, t));
    if (!candidates.length) return null;

    candidates.sort((a, b) => (tableSeats(a) - tableSeats(b)) || (Number(a.id) - Number(b.id)));

    const chosen = candidates[0];
    const chosenSeats = tableSeats(chosen);
    const waste = chosenSeats - p;

    // 0..100 fit quality: 100 when the table fits exactly, decreasing with wasted seats
    // (floored at 40 so even a loose-but-valid fit never reads as a failing match).
    const score = waste === 0 ? 100 : Math.max(40, 100 - waste * 15);

    const reasons = [];
    reasons.push(waste === 0
        ? `מתאים בדיוק ל-${p} סועדים`
        : `${chosenSeats} מקומות ל-${p} סועדים (${waste} פנויים)`);
    // At least one OTHER fitting free table is bigger → seating here keeps the big tables free.
    if (candidates.slice(1).some((t) => tableSeats(t) > chosenSeats)) {
        reasons.push('משאיר שולחנות גדולים יותר פנויים לקבוצות גדולות');
    }
    reasons.push(`פנוי לכל משך ההזמנה (${effectiveDuration(chosen, durationMin)} דק׳)`);
    if (areaId != null) reasons.push('באזור המבוקש');

    const alternatives = candidates.slice(1, 3).map((t) => ({
        id: t.id,
        name: t.name ?? t.id,
        seats: tableSeats(t),
    }));

    return { table: chosen, score, reasons, alternatives };
}

/*
 * freeTablesFor — ALL free tables (not just the single best) for a party at a time, for the
 * "choose a table" list. Same filters as suggestNextTable (unlocked, in-area, free for the
 * whole window) but does NOT drop tables smaller than the party — the operator may still seat
 * them by choice; those are sorted last and flagged `fits:false`. Fitting tables come first
 * (least wasted seats), then the rest (largest first). Item: { id, name, seats, areaId, fits }.
 */
export function freeTablesFor(reservations, tables, { atMinutes, party, areaId = null, durationMin }) {
    const free = (tables || [])
        .filter((t) => !t.is_locked)
        .filter((t) => (areaId == null || Number(t.area_id) === Number(areaId)))
        // Pass the table so its per-table holdTime (if any) is honored; else falls back to durationMin.
        .filter((t) => isTableFree(reservations, t.id, atMinutes, durationMin, t))
        .map((t) => ({
            id: t.id,
            name: t.name ?? String(t.id),
            seats: tableSeats(t),
            areaId: Number(t.area_id) || 0,
            fits: tableSeats(t) >= Number(party || 0),
        }));
    free.sort((a, b) => {
        if (a.fits !== b.fits) return a.fits ? -1 : 1;                 // fitting tables first
        if (a.fits) return (a.seats - b.seats) || (Number(a.id) - Number(b.id)); // least waste
        return (b.seats - a.seats) || (Number(a.id) - Number(b.id));  // non-fitting: largest first
    });
    return free;
}

// Quick day-level KPIs used by the board header.
export function occupancyAt(reservations, tables, atMinutes, durationMin) {
    const state = floorStateAt(reservations, tables, atMinutes, durationMin);
    let occupiedTables = 0;
    let seatedCovers = 0;
    state.forEach((cell) => {
        if (cell.status === 'occupied') { occupiedTables += 1; seatedCovers += Number(cell.res?.party || 0); }
    });
    const totalTables = (tables || []).length;
    return { occupiedTables, freeTables: totalTables - occupiedTables, totalTables, seatedCovers };
}
